/**
 * Cloudflare Turnstile Captcha Component
 * Provides invisible captcha verification - Web version for React Native Web
 */

import React, { useEffect, useRef, useState } from 'react';

// Declare Turnstile types
declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: TurnstileOptions) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
      getResponse: (widgetId: string) => string;
    };
    onTurnstileLoad?: () => void;
  }
}

interface TurnstileOptions {
  sitekey: string;
  callback?: (token: string) => void;
  'error-callback'?: () => void;
  'expired-callback'?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact' | 'flexible';
  action?: string;
  cData?: string;
  language?: string;
  tabindex?: number;
  'response-field'?: boolean;
  'response-field-name'?: string;
  retry?: 'auto' | 'never';
  'retry-interval'?: number;
}

export interface TurnstileCaptchaProps {
  siteKey: string;
  onVerify: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact' | 'flexible';
  action?: string;
  autoRender?: boolean;
}

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

/**
 * Cloudflare Turnstile Captcha Component
 * Uses native HTML elements to ensure compatibility
 */
export const TurnstileCaptcha: React.FC<TurnstileCaptchaProps> = ({
  siteKey,
  onVerify,
  onError,
  onExpire,
  theme = 'auto',
  size = 'normal',
  action,
  autoRender = true,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(false);
  const widgetIdRef = useRef<string | null>(null);
  const containerIdRef = useRef<string>(`turnstile-${Math.random().toString(36).substr(2, 9)}`);

  // Load Turnstile script
  useEffect(() => {
    console.log('🔧 [Turnstile] Initializing script loading...');
    const scriptId = 'turnstile-script';

    // Check if script already exists and turnstile is available
    if (document.getElementById(scriptId) && window.turnstile) {
      console.log('✅ [Turnstile] Script already loaded and turnstile available, setting state');
      setIsLoaded(true);
      return;
    }

    // If script exists but turnstile not available, wait for it
    if (document.getElementById(scriptId)) {
      console.log('⏳ [Turnstile] Script exists but turnstile not ready, waiting...');
      const checkTurnstile = () => {
        if (window.turnstile) {
          console.log('✅ [Turnstile] Turnstile now available after script load');
          setIsLoaded(true);
        } else {
          console.log('⏳ [Turnstile] Still waiting for turnstile...');
          setTimeout(checkTurnstile, 100);
        }
      };
      checkTurnstile();
      return;
    }

    console.log('📥 [Turnstile] Loading Turnstile script from:', TURNSTILE_SCRIPT_URL);
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      console.log('✅ [Turnstile] Script loaded successfully');
      // Wait for window.turnstile to be available
      const checkTurnstile = () => {
        if (window.turnstile) {
          console.log('✅ [Turnstile] Turnstile API is now available');
          setIsLoaded(true);
        } else {
          console.log('⏳ [Turnstile] Waiting for turnstile API to be available...');
          setTimeout(checkTurnstile, 50);
        }
      };
      checkTurnstile();
    };

    script.onerror = () => {
      console.error('❌ [Turnstile] Failed to load script');
      setScriptError(true);
      onError?.();
    };

    document.head.appendChild(script);

    return () => {
      console.log('🧹 [Turnstile] Component unmounting, cleaning up...');
      // Cleanup on unmount
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
          console.log('✅ [Turnstile] Widget cleaned up successfully');
        } catch (e) {
          console.warn('[Turnstile] Error removing widget:', e);
        }
      }
    };
  }, []);

  // Render widget when script is loaded
  useEffect(() => {
    console.log('🎨 [Turnstile] Widget render effect triggered:', {
      isLoaded,
      autoRender,
      hasTurnstile: !!window.turnstile,
      hasExistingWidget: !!widgetIdRef.current,
      containerId: containerIdRef.current,
      siteKey: siteKey.substring(0, 20) + '...'
    });

    if (!isLoaded) {
      console.log('⏳ [Turnstile] Script not loaded yet, waiting...');
      return;
    }

    if (!autoRender) {
      console.log('🚫 [Turnstile] Auto render disabled');
      return;
    }

    if (!window.turnstile) {
      console.error('❌ [Turnstile] window.turnstile not available even though script is loaded');
      return;
    }

    // Prevent multiple renders
    if (widgetIdRef.current) {
      console.log('⚠️ [Turnstile] Widget already exists with ID:', widgetIdRef.current, 'skipping render');
      return;
    }

    // Wait for DOM element to be available
    const renderWidget = () => {
      console.log('🔍 [Turnstile] Looking for container element:', containerIdRef.current);
      const container = document.getElementById(containerIdRef.current);

      if (!container) {
        console.warn('⏳ [Turnstile] Container not found, retrying in 100ms...');
        setTimeout(renderWidget, 100);
        return;
      }

      // Check if container already has content and clear it
      if (container.innerHTML && container.innerHTML.trim() !== '') {
        console.log('⚠️ [Turnstile] Container already has content, clearing it first');
        container.innerHTML = '';
      }

      console.log('✅ [Turnstile] Container found and ready, starting widget render');

      try {
        console.log('🔐 [Turnstile] ====== WIDGET RENDER START ======');
        console.log('🔐 [Turnstile] Rendering widget with config:', {
          siteKey: siteKey,
          siteKeyLength: siteKey.length,
          siteKeyPreview: siteKey.substring(0, 20) + '...',
          isTestKey: siteKey.startsWith('1x00000000'),
          isCloudflareKey: siteKey.startsWith('0x4'),
          theme,
          size,
          action,
          containerId: containerIdRef.current
        });

        const widgetId = window.turnstile.render(container, {
          sitekey: siteKey,
          callback: (token: string) => {
            console.log('✅ [Turnstile] ====== CAPTCHA SUCCESS ======');
            console.log('✅ [Turnstile] Captcha verified, calling onVerify with token:', {
              tokenLength: token.length,
              tokenPreview: token.substring(0, 20) + '...',
              timestamp: new Date().toISOString()
            });
            onVerify(token);
          },
          'error-callback': () => {
            console.error('❌ [Turnstile] ====== CAPTCHA ERROR ======');
            console.error('❌ [Turnstile] Captcha error occurred');
            onError?.();
          },
          'expired-callback': () => {
            console.warn('⚠️ [Turnstile] ====== CAPTCHA EXPIRED ======');
            console.warn('⚠️ [Turnstile] Captcha expired');
            onExpire?.();
          },
          theme,
          size,
          action,
        });

        widgetIdRef.current = widgetId;
        console.log('✅ [Turnstile] Widget rendered successfully with ID:', widgetId);
        console.log('✅ [Turnstile] ====== WIDGET RENDER SUCCESS ======');
      } catch (error) {
        console.error('❌ [Turnstile] ====== WIDGET RENDER ERROR ======');
        console.error('❌ [Turnstile] Error rendering widget:', error);
        setScriptError(true);
        onError?.();
      }
    };

    // Start rendering process
    console.log('⏰ [Turnstile] Starting widget render in 100ms...');
    setTimeout(renderWidget, 100);

    // Cleanup function for this effect
    return () => {
      console.log('🧹 [Turnstile] Widget render effect cleanup');
      if (widgetIdRef.current && window.turnstile) {
        try {
          console.log('🗑️ [Turnstile] Removing widget in effect cleanup:', widgetIdRef.current);
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
          console.log('✅ [Turnstile] Widget removed in effect cleanup');
        } catch (e) {
          console.warn('[Turnstile] Error removing widget in effect cleanup:', e);
        }
      }
    };
  }, [isLoaded, autoRender]); // Reduced dependencies to prevent unnecessary re-renders

  if (scriptError) {
    return React.createElement('div', {
      style: {
        color: 'red',
        padding: '8px',
        fontSize: '12px',
        textAlign: 'center',
        border: '1px solid #ffcdd2',
        borderRadius: '4px',
        backgroundColor: '#ffebee',
      }
    }, 'Failed to load captcha. Please refresh the page.');
  }

  // Use React.createElement to create a proper HTML div
  return React.createElement('div', {
    id: containerIdRef.current,
    style: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      margin: '8px 0',
      minHeight: size === 'compact' ? '65px' : '65px',
    }
  });
};

export default TurnstileCaptcha;
